import { motion } from "motion/react";
import Header from "../../../components/feature/Header";
import Footer from "../../../components/feature/Footer";
import WhatsAppFloating from "../../../components/feature/WhatsAppFloating";
import CookieBanner from "../../../components/feature/CookieBanner";
import QuoteFormSection from "../../home/components/QuoteFormSection";
import SEO from "@/components/feature/SEO";

const reparaciones = [
  "Lonas rotas, manchadas o desgastadas",
  "Motores que no responden o son ruidosos",
  "Brazos articulados rotos o deformados",
  "Sistemas de mando perdidos o estropeados",
  "Sensores de viento/sol averiados",
  "Estructura desnivelada o oxidada",
];

const sintomas = [
  { emoji: "🔄", title: "No se extiende o repliega bien", desc: "El toldo se atasca, va a trompicones o solo recoge a medias. Signo claro de motor o mecanismo dañado." },
  { emoji: "🔊", title: "Hace ruidos extraños al motorizar", desc: "Chirridos, golpes o vibraciones anormales. Puede ser engranaje desgastado, motor forzado o brazo desajustado." },
  { emoji: "⬇️", title: "Lona caída o destensada", desc: "La lona cuelga por un lado, se hincha con el viento o no queda tensa. El muelle interno o el eje de enrollado necesita revisión." },
  { emoji: "☀️", title: "Tela manchada o decolorada", desc: "El sol de Málaga decolora las lonas de baja calidad. Una lona manchada o amarillenta arruina la estética de tu fachada." },
  { emoji: "📵", title: "Sensor no responde a viento", desc: "El toldo no se recoge automáticamente cuando sopla fuerte. El sensor de viento puede estar roto, desajustado o con la batería agotada." },
];

const decisiones = [
  { caso: "Lona &lt;5 años, motor OK", accion: "Cambiar lona (200-500€)", recomendado: true },
  { caso: "Motor averiado, estructura OK", accion: "Cambiar motor (250-400€)", recomendado: true },
  { caso: "Estructura oxidada", accion: "Sustituir todo", recomendado: false },
  { caso: "+10 años con múltiples fallos", accion: "Sustituir todo", recomendado: false },
];

const precios = [
  { servicio: "Cambio de lona estándar", precio: "200 - 500 €", emoji: "🧵" },
  { servicio: "Reparar motor existente", precio: "100 - 180 €", emoji: "🔧" },
  { servicio: "Sustituir motor Somfy", precio: "250 - 400 €", emoji: "⚡" },
  { servicio: "Reparación brazos", precio: "150 - 300 €", emoji: "🦾" },
  { servicio: "Sensor viento/sol", precio: "80 - 150 €", emoji: "🌬️" },
];

const faqs = [
  {
    q: "¿Reparáis cualquier marca de toldo?",
    a: "Sí, reparamos todas las marcas: Somfy, Markilux, Llaza, Gaviota, Simbac, Tempotest y genéricos. Tenemos recambios compatibles para la mayoría de motores y mecanismos del mercado. Si es una marca muy antigua o desconocida, lo evaluamos gratis.",
  },
  {
    q: "¿Cuánto tarda la reparación?",
    a: "Reparaciones simples (mando, sensor, ajuste): mismo día o 24h. Cambio de motor: 24-48h si tenemos el recambio en stock. Cambio de lona: 3-5 días hábiles (hay que pedir la lona a medida). Te damos fecha exacta tras diagnóstico.",
  },
  {
    q: "¿Tenéis recambios en stock?",
    a: "Sí, mantenemos stock habitual de motores Somfy más comunes, sensores de viento/sol, mandos universales y mecanismos de brazos. Para lonas, las pedimos a medida según color y dimensiones exactas. Plazo: 2-4 días hábiles.",
  },
  {
    q: "¿Da garantía la reparación?",
    a: "Sí. Motor nuevo Somfy: 5 años garantía oficial. Motor reparado: 1 año. Brazos y mecanismos reparados: 2 años. Lona nueva: 3 años. Mano de obra: 2 años. Todo por escrito. Si el mismo fallo vuelve en garantía, lo arreglamos gratis.",
  },
  {
    q: "¿Trabajáis con comunidades de vecinos?",
    a: "Claro. Reparamos toldos comunitarios de portales, patios interiores y azoteas. Emitimos factura con IVA para la comunidad. Podemos hacer presupuesto para varias unidades con descuento por volumen. También ofrecemos mantenimiento anual programado para comunidades.",
  },
];

export default function ReparacionToldos() {
  return (
    <div className="min-h-screen bg-white">
      <SEO
        title="Reparación Toldos Málaga | Lonas, Motores, Brazos"
        description="Reparamos toldos en Málaga: cambio de lona, motor estropeado, brazos rotos. Servicio rápido. Presupuesto gratis. ☎ 951 12 07 70"
        canonical="/toldos/reparacion-toldos/"
        breadcrumbs={[{ name: "Inicio", path: "/" }, { name: "Toldos", path: "/toldos" }, { name: "Reparación de toldos" }]}
      />
      <Header />
      <main>
        {/* HERO */}
        <section className="bg-white pt-24 md:pt-[72px]">
          <div className="max-w-container mx-auto px-4 md:px-6 py-12 md:py-24">
            <div className="flex flex-col lg:flex-row items-center gap-10 lg:gap-16">
              <div className="w-full lg:w-[60%] flex flex-col gap-6">
                <div className="inline-flex items-center gap-2 bg-accent text-text-primary font-body font-semibold text-sm px-4 py-2 rounded-pill w-fit">
                  <span>⚡</span> 24h · Respuesta 20 min
                </div>
                <h1 className="font-heading font-extrabold text-4xl md:text-5xl lg:text-[56px] leading-[1.1] text-text-primary">
                  Reparación de Toldos en Málaga
                </h1>
                <p className="font-body text-lg md:text-xl text-text-secondary leading-relaxed max-w-xl">
                  Reparamos todos los modelos: lonas rotas, motores estropeados, brazos dañados. Servicio rápido.
                </p>
                <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
                  <a
                    href="tel:+34951120770"
                    className="inline-flex items-center gap-2 bg-primary hover:bg-primary-dark text-white font-body font-bold text-lg md:text-xl px-8 py-4 rounded-pill transition-colors whitespace-nowrap"
                  >
                    <span>📞</span> 951 12 07 70
                  </a>
                  <a
                    href="#presupuesto"
                    className="inline-flex items-center gap-2 border-2 border-secondary text-secondary hover:bg-secondary hover:text-white font-body font-semibold text-base px-6 py-3.5 rounded-pill transition-colors whitespace-nowrap"
                  >
                    Pedir presupuesto
                  </a>
                </div>
                <div className="flex flex-wrap items-center gap-4 md:gap-6 mt-2">
                  <span className="flex items-center gap-1.5 font-body font-medium text-sm text-text-secondary"><span className="text-green-600">✓</span> +10 años · desde 2015</span>
                  <span className="flex items-center gap-1.5 font-body font-medium text-sm text-text-secondary"><span className="text-green-600">✓</span> Todas las marcas</span>
                  <span className="flex items-center gap-1.5 font-body font-medium text-sm text-text-secondary"><span className="text-green-600">✓</span> Garantía reparación</span>
                </div>
              </div>
              <div className="w-full lg:w-[40%] relative">
                <div className="relative rounded-3xl overflow-hidden lg:-rotate-1 shadow-xl">
                  <div className="absolute inset-0 bg-blue-900/35 z-10 pointer-events-none"></div>
                  <img
                    src="https://images.unsplash.com/photo-1600585154340-be6161a56a0c?w=1200&q=80&auto=format&fit=crop"
                    alt="Reparación profesional de toldos en Málaga"
                    className="w-full h-auto object-cover aspect-[4/3]"
                    loading="eager"
                  />
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* ¿QUÉ REPARAMOS? */}
        <section className="bg-surface-alt py-16 md:py-24">
          <div className="max-w-container mx-auto px-4 md:px-6">
            <div className="flex flex-col lg:flex-row gap-10 lg:gap-16 items-start">
              <div className="w-full lg:w-1/2">
                <h2 className="font-heading font-bold text-3xl md:text-4xl text-text-primary mb-4">¿Qué reparamos?</h2>
                <div className="w-10 h-1 bg-primary rounded-full mb-6"></div>
                <p className="font-body text-lg text-text-secondary leading-relaxed mb-8">
                  Desde el cambio de una lona hasta la sustitución completa del motor. Si tu toldo tiene un problema, lo resolvemos.
                </p>
                <div className="flex flex-col gap-3">
                  {reparaciones.map((item, i) => (
                    <motion.div
                      key={i}
                      className="flex items-center gap-4 bg-white rounded-xl px-5 py-4 shadow-sm"
                      initial={{ opacity: 0, x: -15 }}
                      whileInView={{ opacity: 1, x: 0 }}
                      viewport={{ once: true }}
                      transition={{ duration: 0.4, delay: i * 0.08 }}
                    >
                      <span className="flex items-center justify-center w-8 h-8 bg-primary/10 text-primary rounded-full shrink-0 font-bold text-sm">✓</span>
                      <span className="font-body text-base text-text-primary">{item}</span>
                    </motion.div>
                  ))}
                </div>
              </div>
              <div className="w-full lg:w-1/2">
                <img
                  src="https://images.unsplash.com/photo-1600585154340-be6161a56a0c?w=1200&q=80&auto=format&fit=crop"
                  alt="Piezas de toldo en reparación"
                  className="w-full rounded-3xl object-cover aspect-[7/5] shadow-xl"
                  loading="lazy"
                />
              </div>
            </div>
          </div>
        </section>

        {/* SÍNTOMAS */}
        <section className="bg-white py-16 md:py-24">
          <div className="max-w-container mx-auto px-4 md:px-6">
            <div className="text-center mb-12">
              <h2 className="font-heading font-bold text-3xl md:text-4xl text-text-primary mb-4">Síntomas de un toldo a reparar</h2>
              <div className="w-10 h-1 bg-primary mx-auto rounded-full"></div>
              <p className="font-body text-lg text-text-secondary max-w-2xl mx-auto mt-4">
                Si notas alguno de estos signos, llámanos antes de que el daño sea mayor y más caro.
              </p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 max-w-5xl mx-auto">
              {sintomas.map((s, i) => (
                <motion.div
                  key={s.title}
                  className="bg-surface-alt rounded-[20px] p-8 shadow-sm hover:shadow-md transition-shadow border-l-[6px] border-primary"
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: i * 0.1 }}
                >
                  <div className="text-3xl mb-3">{s.emoji}</div>
                  <h3 className="font-heading font-bold text-lg text-text-primary mb-2">{s.title}</h3>
                  <p className="font-body text-sm text-text-secondary leading-relaxed">{s.desc}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* ¿REPARAR O SUSTITUIR? */}
        <section className="bg-surface-alt py-16 md:py-24">
          <div className="max-w-container mx-auto px-4 md:px-6">
            <div className="text-center mb-12">
              <h2 className="font-heading font-bold text-3xl md:text-4xl text-text-primary mb-4">¿Reparar o sustituir?</h2>
              <div className="w-10 h-1 bg-primary mx-auto rounded-full"></div>
              <p className="font-body text-lg text-text-secondary max-w-2xl mx-auto mt-4">
                Te ayudamos a decidir qué opción es más económica y sensata según el estado de tu toldo.
              </p>
            </div>
            <div className="max-w-3xl mx-auto bg-white rounded-3xl overflow-hidden shadow-sm">
              <div className="grid grid-cols-1 sm:grid-cols-2 border-b border-border">
                <div className="px-6 md:px-8 py-4 bg-primary/5 font-heading font-bold text-base text-text-primary">Caso</div>
                <div className="px-6 md:px-8 py-4 bg-primary/5 font-heading font-bold text-base text-text-primary">Acción recomendada</div>
              </div>
              {decisiones.map((d, i) => (
                <div key={i} className={`grid grid-cols-1 sm:grid-cols-2 ${i !== decisiones.length - 1 ? "border-b border-border" : ""}`}>
                  <div className="px-6 md:px-8 py-5 font-body text-sm text-text-primary">{d.caso}</div>
                  <div className="px-6 md:px-8 py-5">
                    <span className={`inline-block font-body text-sm font-semibold px-3 py-1 rounded-full ${d.recomendado ? "bg-green-100 text-green-700" : "bg-red-100 text-red-700"}`}>
                      {d.accion}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* PRECIOS */}
        <section className="bg-white py-16 md:py-24">
          <div className="max-w-container mx-auto px-4 md:px-6">
            <div className="text-center mb-12">
              <h2 className="font-heading font-bold text-3xl md:text-4xl text-text-primary mb-4">Precios de reparación</h2>
              <div className="w-10 h-1 bg-primary mx-auto rounded-full"></div>
              <p className="font-body text-lg text-text-secondary mt-4 max-w-2xl mx-auto">
                Diagnóstico gratis. Solo pagas si aceptas la reparación.
              </p>
            </div>
            <div className="max-w-2xl mx-auto bg-surface-alt rounded-3xl overflow-hidden">
              {precios.map((p, i) => (
                <div
                  key={p.servicio}
                  className={`flex items-center justify-between px-6 md:px-8 py-5 ${i !== precios.length - 1 ? "border-b border-border" : ""}`}
                >
                  <span className="flex items-center gap-3 font-body text-base md:text-lg text-text-primary">
                    <span>{p.emoji}</span> {p.servicio}
                  </span>
                  <span className="font-heading font-bold text-lg md:text-xl text-primary">{p.precio}</span>
                </div>
              ))}
            </div>
            <p className="text-center font-body text-sm text-text-secondary mt-6">
              Precios orientativos según modelo, dimensiones y gravedad del daño. Presupuesto exacto tras diagnóstico gratuito.
            </p>
          </div>
        </section>

        {/* CTA */}
        <section className="bg-primary py-16 md:py-24">
          <div className="max-w-container mx-auto px-4 md:px-6 text-center">
            <h2 className="font-heading font-bold text-3xl md:text-4xl text-white mb-4">¿Tu toldo necesita reparación?</h2>
            <p className="font-body text-lg text-white/90 max-w-xl mx-auto mb-8">
              Diagnóstico gratis en 24h. Reparamos todas las marcas con garantía. Sin sorpresas.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <a
                href="tel:+34951120770"
                className="inline-flex items-center gap-2 bg-white hover:bg-slate-100 text-primary font-body font-bold text-lg px-8 py-4 rounded-pill transition-colors whitespace-nowrap"
              >
                <span>📞</span> 951 12 07 70
              </a>
              <a
                href="#presupuesto"
                className="inline-flex items-center gap-2 border-2 border-white text-white hover:bg-white hover:text-primary font-body font-semibold text-base px-6 py-3.5 rounded-pill transition-colors whitespace-nowrap"
              >
                Pedir presupuesto online
              </a>
            </div>
          </div>
        </section>

        {/* FAQ */}
        <section className="bg-white py-16 md:py-24">
          <div className="max-w-container mx-auto px-4 md:px-6">
            <div className="max-w-3xl mx-auto" itemScope itemType="https://schema.org/FAQPage">
              <h2 className="font-heading font-bold text-3xl md:text-4xl text-text-primary text-center mb-12">Preguntas frecuentes sobre reparación</h2>
              <div className="flex flex-col gap-4">
                {faqs.map((f, i) => (
                  <motion.div
                    key={i}
                    className="bg-surface-alt rounded-2xl p-6"
                    initial={{ opacity: 0, y: 10 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.4, delay: i * 0.08 }}
                    itemScope
                    itemProp="mainEntity"
                    itemType="https://schema.org/Question"
                  >
                    <h3 className="font-heading font-bold text-lg text-text-primary mb-2" itemProp="name">{f.q}</h3>
                    <div itemScope itemProp="acceptedAnswer" itemType="https://schema.org/Answer">
                      <p className="font-body text-sm text-text-secondary leading-relaxed" itemProp="text">{f.a}</p>
                    </div>
                  </motion.div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* FORM */}
        <div id="presupuesto">
          <QuoteFormSection />
        </div>
      </main>
      <Footer />
      <WhatsAppFloating />
      <CookieBanner />
    </div>
  );
}